// AccountSetting.jsx
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Icons } from '@/icons';
import { useDispatch } from 'react-redux';
import { fetchProfile, updateProfile } from '@/store/User';
import { toast } from 'react-toastify';
import { Button, Dialog, DialogActions, DialogContent } from '@mui/material';
import ChangePasswordDialog from './ChangePasswordDialog';
import { formatDateYYYYMMDD, formatLocalDate } from '@/utils/FormateLocalDate';

type Profile = {
  name: string;
  phone: string;
  date_of_birth: string;
  email: string;
};

const AccountSetting = () => {
  const [profile, setProfile] = useState<Profile>({
    name: '',
    phone: '',
    date_of_birth: '',
    email: '',
  });

  const dateInputRef = useRef<HTMLInputElement | null>(null);

  const openDatePicker = () => {
    if (dateInputRef.current?.showPicker) {
      dateInputRef.current.showPicker();
    } else {
      dateInputRef.current?.focus();
      dateInputRef.current?.click();
    }
  };

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [type, setType] = useState('');
  const [editProfile, setEditProfile] = useState();
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState('');
  const [inputType, setInputType] = useState('');
  const [pOpen, setPOpen] = useState(false);

  const dispatch = useDispatch<any>();

  useEffect(() => {
    const fetchData = async () => {
      const res = await dispatch(fetchProfile());
      const data = res.payload.data[0];
      setProfile(data);

      // Set initial form states
      setName(data.name);
      setPhone(data.phone);
      setDateOfBirth(data.date_of_birth);
    };

    fetchData();
  }, [editProfile]);

  const isEdited = useMemo(() => {
    return (
      name !== profile.name ||
      phone !== profile.phone ||
      dateOfBirth !== profile.date_of_birth
    );
  }, [name, phone, dateOfBirth, profile]);

  const submit = async () => {
    const profileData = {
      ...(name !== profile.name && { name }),
      ...(phone !== profile.phone && { phone }),
      ...(dateOfBirth !== profile.date_of_birth && { dateOfBirth: formatLocalDate(dateOfBirth) }),
    };

    try {
      const res = await dispatch(updateProfile(profileData));
      setEditProfile(res.payload.data);
    } catch (error) {
      toast.error('❌ Profile update failed');
      console.error('Update error:', error);
    }
  };

  const ok = async () => {
    setOpen(false);
    switch (type) {
      case 'name':
        setName(val);
        break;
      case 'phone':
        setPhone(val);
        break;
      case 'dateOfBirth':
        setDateOfBirth(val);
        break;
      default:
        return null;
    }
  };

  const labelType = () => {
    switch (type) {
      case 'name':
        return 'Inter Name';
      case 'phone':
        return 'Inter Phone Number';
      case 'dateOfBirth':
        return 'Inter Date of Birth ( Date Format: DD-MM-YYYY )';
      default:
        return 'Invalid';
    }
  };

  return (
    <div className='w-full h-full bg-white rounded-[24px] shadow p-6'>
      {/* Fields Display - UI untouched */}
      <div className='flex items-center justify-between py-4 border-b border-gray-200'>
        <div className='flex items-center'>
          <Icons.SettingUser className='text-gray-600 text-2xl mr-3' />
          <div className='flex flex-col'>
            <span className='text-xs text-gray-500'>Name</span>
            <span className='text-base text-gray-900'>{name || 'null'}</span>
          </div>
          <Icons.SettingEdit
            className='ml-4 mb-[-20px] w-[28px]'
            onClick={() => {
              setVal(name || '');
              setInputType('text');
              setOpen(true);
              setType('name');
            }}
          />
        </div>
      </div>

      <div className='flex items-center justify-between py-4 border-b border-gray-200 last:border-b-0'>
        <div className='flex items-center'>
          <Icons.SettingMail className='text-gray-600 text-2xl mr-3' />
          <div className='flex flex-col'>
            <span className='text-xs text-gray-500'>Email</span>
            <span className='text-base text-gray-900'>
              {(profile && profile.email) || 'null'}
            </span>
          </div>
        </div>
      </div>

      <div className='flex items-center justify-between py-4 border-b border-gray-200'>
        <div className='flex items-center'>
          <Icons.SettingPhone className='text-gray-600 text-2xl mr-3' />
          <div className='flex flex-col'>
            <span className='text-xs text-gray-500'>Phone Number</span>
            <span className='text-base text-gray-900'>{phone || 'null'}</span>
          </div>
          <Icons.SettingEdit
            className='ml-4 mb-[-20px] w-[28px]'
            onClick={() => {
              setVal(phone || '');
              setInputType('number');
              setOpen(true);
              setType('phone');
            }}
          />
        </div>
      </div>

      <div className='flex items-center justify-between py-4 border-b border-gray-200'>
        <div className='flex items-center'>
          <Icons.SettingDOB className='text-gray-600 text-2xl mr-3' />
          <div className='flex flex-col'>
            <span className='text-xs text-gray-500'>Date of Birth</span>
            <span className='text-base text-gray-900'>
              {formatLocalDate(dateOfBirth) || 'null'}
            </span>
          </div>
          <Icons.SettingEdit
            className='ml-4 mb-[-20px] w-[28px]'
            onClick={() => {
              setVal(formatDateYYYYMMDD(dateOfBirth) || ''); // preload current dob in dialog
              setOpen(true);
              setType('dateOfBirth');
            }}
          />
        </div>
      </div>

      <div className='flex items-center justify-between py-4 border-b border-gray-200'>
        <div className='flex items-center'>
          <Icons.SettingLock className='text-gray-600 text-2xl mr-3' />
          <div className='flex flex-col'>
            <span className='text-xs text-gray-500'>Password</span>
            <span className='text-base text-gray-900'>********</span>
          </div>
          <Icons.SettingEdit
            className='ml-4 mb-[-20px] w-[28px]'
            onClick={() => {
              setPOpen(true);
            }}
          />
        </div>
      </div>
      {/* Change password  */}
      <ChangePasswordDialog
        pOpen={pOpen}
        cancelLabel='No, Cancel'
        onClose={() => setPOpen(false)}
      />

      {/* Modal */}
      <Dialog
        open={open}
        PaperProps={{
          sx: { borderRadius: '24px', padding: '30px' },
        }}
        onClose={() => setOpen(false)}
        maxWidth='xs'
        fullWidth
      >
        <DialogContent sx={{ textAlign: 'center' }}>
          <label
            htmlFor='custom-input'
            style={{
              display: 'block',
              textAlign: 'left',
              marginBottom: '6px',
              fontWeight: '500',
              color: '#444',
              fontSize: '14px',
            }}
          >
            {labelType()}
          </label>
          {type === 'dateOfBirth' ? (
            <input
              ref={dateInputRef}
              type='date'
              value={val}
              style={{
                width: '100%',
                padding: '10px 14px',
                fontSize: '16px',
                borderRadius: '8px',
                border: '1px solid #ccc',
                outline: 'none',
                marginTop: '10px',
                boxSizing: 'border-box',
                transition: 'border-color 0.3s',
              }}
              onFocus={(e) => (e.target.style.borderColor = '#1976d2')}
              onBlur={(e) => (e.target.style.borderColor = '#ccc')}
              onClick={openDatePicker}
              onChange={(e) => setVal(e.target.value)} // "YYYY-MM-DD"
            />
          ) : (
            <input
              type={inputType}
              placeholder='Enter value'
              value={val}
              style={{
                width: '100%',
                padding: '10px 14px',
                fontSize: '16px',
                borderRadius: '8px',
                border: '1px solid #ccc',
                outline: 'none',
                marginTop: '10px',
                boxSizing: 'border-box',
                transition: 'border-color 0.3s',
              }}
              onFocus={(e) => (e.target.style.borderColor = '#1976d2')}
              onBlur={(e) => (e.target.style.borderColor = '#ccc')}
              onChange={(e) => setVal(e.target.value)}
            />
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 2 }}>
          <Button
            variant='outlined'
            disabled={!val}
            onClick={ok}
            sx={{
              textTransform: 'none',
              borderRadius: 100,
              px: 4,
              color: 'black',
              borderColor: 'black',
            }}
          >
            OK
          </Button>
          <Button
            variant='contained'
            onClick={() => setOpen(false)}
            sx={{
              textTransform: 'none',
              borderRadius: 100,
              px: 4,
              bgcolor: '#FF0050',
              '&:hover': { bgcolor: '#E6004F' },
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Save Button */}
      <div className='mt-6 text-right'>
        <button
          onClick={submit}
          disabled={!isEdited}
          className={`px-6 py-2 rounded-lg font-medium text-white transition ${
            isEdited
              ? 'bg-blue-600 hover:bg-blue-700'
              : 'bg-gray-300 cursor-not-allowed'
          }`}
        >
          Save Changes
        </button>
      </div>
    </div>
  );
};

export default AccountSetting;
